import React, { useMemo } from 'react';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import GenericBarChart from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/PathwaysTab/GenericBarChart';
import { METRICTYPE as MetricType } from 'src/utils/entityDefinitions';
import { getSimpleTooltipDiv } from 'src/components/EntityPage/Renderer/tooltipDiv';
import { convertBrandedSearchRateByCampaignDataToCsv } from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/AudiencesTab/BrandedSearchRateByCampaign/utils';
import { generateCampaignLinkForChart } from 'src/components/Layout/Advertising/ConnectPageLayout/visualizations/NewToBrand/utils';

interface BrandedSearchRateByCampaignBarChartProps {
  data: any;
}

/**
 *
 * @returns Bar chart displaying time to conversion for [metric]
 */
const BrandedSearchRateByCampaignBarChart = ({ data }: BrandedSearchRateByCampaignBarChartProps) => {
  const { mainTimePeriod, adCampaigns } = useAppSelector((state) => state);
  const formatMetric = useMetricFormatter();

  const chartProps: Highcharts.Options = useMemo(
    () => ({
      horizontalScrolling: {
        enabled: true,
        step: 3,
        scrollStyles: {
          top: '225px' // Used to adjust the scroll controls container
        }
      },
      chart: {
        spacingLeft: 8
      },
      legend: {
        enabled: false
      },
      exporting: {
        enabled: true
      },
      title: {
        text: 'Branded Search Rate By Campaign',
        x: -10
      },
      xAxis: {
        type: 'category',
        max: 5,
        labels: {
          useHTML: true,
          formatter() {
            const campaignName = this.value;

            // Search our campaign collection from Redux to match the campaign name to the campaign details
            const campaignMatch = adCampaigns.find((campaign) => campaign.campaignName === campaignName);

            if (campaignMatch) {
              const { campaignId } = campaignMatch;
              return generateCampaignLinkForChart({ mainTimePeriod, campaignName, campaignId });
            } else {
              const convertedName = String(campaignName).replace(/_/g, '-'); // CAMPAIGN_NAME --> CAMPAIGN-NAME. Replace campaign names using "_" in order to properly wrap them.
              return `<a style="margin-top: 5px; display: inline-block; white-space: normal; font-size: 13px;">${convertedName}</a>`;
            }
          }
        }
      },
      yAxis: {
        labels: {
          formatter() {
            return `${this?.value}%`;
          }
        }
      },
      tooltip: {
        shared: true,
        positioner(boxWidth, boxHeight, point) {
          return {
            x: point.plotX + this.chart.plotLeft - boxWidth / 2, // center the tooltip horizontally
            y: point.plotY + this.chart.plotTop - boxHeight - 10 // position the tooltip above the column
          };
        },
        formatter() {
          const primaryValue = this.points[0].y;
          const primaryColor = this.points[0].color as string;

          return getSimpleTooltipDiv([
            {
              label: `${formatMetric(primaryValue / 100, MetricType.PERCENT, {
                decimalPlaces: 2,
                showFullValue: true
              })}`,
              color: primaryColor
            }
          ]);
        }
      },
      plotOptions: {
        barWidth: 65
      }
    }),
    [formatMetric, mainTimePeriod, adCampaigns]
  );

  const columnSeries: Highcharts.SeriesColumnOptions = useMemo(
    () => ({
      type: 'column',
      name: 'name',
      data
    }),
    [data]
  );

  return (
    <GenericBarChart
      columnData={columnSeries}
      chartProps={chartProps}
      convertSeriesToDelimitedData={() =>
        convertBrandedSearchRateByCampaignDataToCsv({ data, dateRange: mainTimePeriod.displayName })
      }
    />
  );
};

export default BrandedSearchRateByCampaignBarChart;
